import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import type { ImpersonationUiState } from '../lib/impersonation';
import { isImpersonationExpired } from '../lib/impersonation';
import AccountSwitcher from './AccountSwitcher';

type Props = {
  state: ImpersonationUiState;
};

function formatEnd(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function ImpersonationBanner({ state }: Props) {
  const { exitImpersonation } = useAuth();
  const [exiting, setExiting] = React.useState(false);
  const [, setTick] = React.useState(0);

  React.useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const endingSoon =
    !isImpersonationExpired(state) &&
    new Date(state.expires_at).getTime() - Date.now() <= 5 * 60 * 1000;

  const handleExit = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      await exitImpersonation();
    } finally {
      setExiting(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed top-0 inset-x-0 z-50 border-b ${
        endingSoon
          ? 'bg-amber-100 border-amber-400'
          : 'bg-amber-50 border-amber-300'
      } text-amber-950`}
    >
      <div className="max-w-full px-3 sm:px-6 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <AlertTriangle className="w-5 h-5 shrink-0 text-amber-700" aria-hidden />
          <span className="text-sm font-medium shrink-0">Viewing as</span>
          <AccountSwitcher variant="banner" />
          <span className="text-sm text-amber-900 shrink-0">
            · ends {formatEnd(state.expires_at)}
            {endingSoon ? ' · Ending soon' : ''}
          </span>
        </div>
        <button
          type="button"
          onClick={handleExit}
          disabled={exiting}
          className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 rounded-md text-sm font-medium bg-amber-900 text-white hover:bg-amber-950 disabled:opacity-60 shrink-0"
        >
          {exiting ? 'Exiting…' : 'Exit to superadmin'}
        </button>
      </div>
    </div>
  );
}
